import { Navigate, Route, BrowserRouter as Router, Routes, useLocation } from "react-router-dom";
import "@/public/styles/main.css";
import "@mdi/font/css/materialdesignicons.css";
import { Layout } from "./components/Layout";
import RootLayout from "./components/layout/root-layout";
import { AuthProvider, useAuth } from "./contexts/auth-context";
import { ThemeProvider } from "./contexts/theme-context";
import { ToastProvider } from "./contexts/toast-context";
import { Login } from "./pages/auth/login";
import { Register } from "./pages/auth/register";
import { AccountReview } from "./pages/auth/account-review";
import { LogsPage } from "@/pages/admin/logs-page";
import { UsersPage } from "@/pages/admin/users-page";
import { TestsPage } from "@/pages/tests/list";
import { TestDetailsPage } from "@/pages/tests/details/details";
import { TestRunsPage } from "@/pages/tests/details/runs";
import { TestFilesPage } from "@/pages/tests/details/files";
import { TestFlowPage } from "@/pages/tests/details/flow";
import { TestExpectationsPage } from "@/pages/tests/details/expectations";
import { TestExplorerPage } from "@/pages/tests/details/explorer";
import { HelpPage } from "@/pages/help";
import CommitsPage from "@/pages/commits";
import MappingsPage from "@/pages/mappings";
import JobsPage from "@/pages/jobs";
import ToolsPage from "@/pages/tools";
import JobFinderPage from "@/pages/job-finder";
import { RunningTestsPage } from "@/pages/running-tests";
import ProtectedLayout from "./pages/layout";
import { useEffect, useState } from "react";
import { api } from "./lib/api";

interface ProtectedRouteProps {
  children: React.ReactNode;
}

function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { user, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <div />;
  }

  if (!user) {
    const currentUrl = window.location.href.replace(window.location.origin, "");
    localStorage.setItem("current_url", currentUrl);
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check if user is inactive - redirect to account review
  if (user.state === "inactive") {
    return <Navigate to="/account-review" replace />;
  }

  return <>{children}</>;
}

// Homepage component that handles the redirect logic
function Homepage() {
  const [redirect, setRedirect] = useState<string | null>(null);

  useEffect(() => {
    const checkUserAndRedirect = async () => {
      const token = localStorage.getItem("auth_token");
      
      // If no token, redirect to login
      if (!token) {
        setRedirect("/login");
        return;
      }

      try {
        // Try to get user data to check if user is active
        const userData = await api.get("/user");
        
        // If successful, user is active - redirect to app
        if (userData) {
          setRedirect("/app");
        }
      } catch (error) {
        // If error (user is inactive), redirect to account review
        setRedirect("/account-review");
      }
    };

    checkUserAndRedirect();
  }, []);

  if (redirect) {
    return <Navigate to={redirect} replace />;
  }

  // Show loading while determining redirect
  return <div />;
}

function AppRoutes() {
  const defaultRoute = "/app/tests";
  
  return (
    <Routes>
      <Route path="/" element={<Homepage />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/account-review" element={<AccountReview />} />
      <Route
        path="/app"
        element={
          <ProtectedRoute>
            <Layout>
              <ProtectedLayout />
            </Layout>
          </ProtectedRoute>
        }
      >
        <Route path="" element={<Navigate to={defaultRoute} replace />} />
        
        {/* Tests routes */}
        <Route path="tests">
          <Route index element={<TestsPage />} />
          <Route path=":testId" element={<TestDetailsPage />} />
          <Route path=":testId/runs" element={<TestRunsPage />} />
          <Route path=":testId/files" element={<TestFilesPage />} />
          <Route path=":testId/flow" element={<TestFlowPage />} />
          <Route path=":testId/expectations" element={<TestExpectationsPage />} />
          <Route path=":testId/explorer" element={<TestExplorerPage />} />
        </Route>

        {/* Running Tests route */}
        <Route path="running-tests" element={<RunningTestsPage />} />

        {/* Help route */}
        <Route path="help" element={<HelpPage />} />

        {/* Commits route */}
        <Route path="commits" element={<CommitsPage />} />
        {/* Mappings route */}
        <Route path="mappings" element={<MappingsPage />} />
        {/* Jobs route */}
        <Route path="jobs" element={<JobsPage />} />
        {/* Tools route */}
        <Route path="tools" element={<ToolsPage />} />
        {/* Job Finder route */}
        <Route path="job-finder" element={<JobFinderPage />} />
        
        {/* Admin routes - only accessible to admins */}
        <Route path="admin">
          <Route path="logs" element={<LogsPage />} />
          <Route path="users" element={<UsersPage />} />
          <Route index element={<Navigate to="/app/admin/logs" replace />} />
        </Route>
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export function App() {
  return (
    <RootLayout>
      <ThemeProvider>
        <ToastProvider>
          <AuthProvider>
            <Router>
              <AppRoutes />
            </Router>
          </AuthProvider>
        </ToastProvider>
      </ThemeProvider>
    </RootLayout>
  );
}
